// Pure-sync reference parser for the local-first BSB Bible (M.3.6.3.c).
//
// Public API: parseReference(text) → ParsedReference | null.
//
// Probe-importable leaf module: imports from `./bsb-types` (relative,
// not @/) so `tsx scripts/probe-bsb-api.mjs` can load it without
// configuring path-map resolution. bsb-types itself has zero internal
// imports.
//
// Operator-locked rejections (per M.3.6.3.c plan):
//   - "Song of Songs" → null. Catholic-leaning name; not in alias map.
//   - "Canticles"     → null. Catholic name; not in alias map.
// These are not bugs; the parser deliberately returns null for both.
//
// Operator-locked acceptances (per M.3.6.3.c plan):
//   - "Psalm" AND "Psalms" → slug 'psalm'.
//   - "Revelation" AND "Revelations" → slug 'revelation'.
//   - "Song of Solomon" AND "sos" → slug 'song-of-solomon'.

import { CANONICAL_BOOKS } from './bsb-types';

export interface ParsedReference {
  bookSlug: string;
  chapter: number;
  /** null when the input was chapter-only ("John 3"). */
  verseStart: number | null;
  /** null when chapter-only; equals verseStart when no range supplied. */
  verseEnd: number | null;
}

// Hand-curated alias map. Every book carries:
//   - canonical full name lowercased
//   - common short abbreviations
//   - (numbered books) Roman / English-ordinal / 1st-2nd-3rd forms
//     with and without internal whitespace
//
// Probe iterates every entry in scripts/probe-bsb-api.mjs and asserts
// each alias resolves to its expected slug. Operator-locked exclusions
// documented above; do not add 'song of songs' or 'canticles'.
const ALIAS_DATA: ReadonlyArray<readonly [readonly string[], string]> = [
  // ---- Old Testament ----
  [['genesis', 'gen', 'gn'], 'genesis'],
  [['exodus', 'exod', 'exo', 'ex'], 'exodus'],
  [['leviticus', 'lev', 'lv'], 'leviticus'],
  [['numbers', 'num', 'nm', 'nb'], 'numbers'],
  [['deuteronomy', 'deut', 'deu', 'dt'], 'deuteronomy'],
  [['joshua', 'josh', 'jos', 'jsh'], 'joshua'],
  [['judges', 'judg', 'jdg', 'jdgs'], 'judges'],
  [['ruth', 'ru', 'rth'], 'ruth'],
  [
    [
      '1 samuel', '1samuel',
      '1 sam', '1sam', '1sa',
      'i samuel', 'isamuel',
      'i sam', 'isam',
      'first samuel', 'firstsamuel',
      'first sam',
      '1st samuel', '1st sam',
    ],
    '1-samuel',
  ],
  [
    [
      '2 samuel', '2samuel',
      '2 sam', '2sam', '2sa',
      'ii samuel', 'iisamuel',
      'ii sam', 'iisam',
      'second samuel', 'secondsamuel',
      'second sam',
      '2nd samuel', '2nd sam',
    ],
    '2-samuel',
  ],
  [
    [
      '1 kings', '1kings',
      '1 kgs', '1kgs', '1kg', '1k',
      'i kings', 'ikings',
      'i kgs', 'ikgs',
      'first kings', 'firstkings',
      'first kgs',
      '1st kings', '1st kgs',
    ],
    '1-kings',
  ],
  [
    [
      '2 kings', '2kings',
      '2 kgs', '2kgs', '2kg', '2k',
      'ii kings', 'iikings',
      'ii kgs', 'iikgs',
      'second kings', 'secondkings',
      'second kgs',
      '2nd kings', '2nd kgs',
    ],
    '2-kings',
  ],
  [
    [
      '1 chronicles', '1chronicles',
      '1 chron', '1chron',
      '1 chr', '1chr', '1ch',
      'i chronicles', 'ichronicles',
      'i chron', 'ichron',
      'i chr', 'ichr',
      'first chronicles', 'firstchronicles',
      'first chr',
      '1st chronicles', '1st chr',
    ],
    '1-chronicles',
  ],
  [
    [
      '2 chronicles', '2chronicles',
      '2 chron', '2chron',
      '2 chr', '2chr', '2ch',
      'ii chronicles', 'iichronicles',
      'ii chron', 'iichron',
      'ii chr', 'iichr',
      'second chronicles', 'secondchronicles',
      'second chr',
      '2nd chronicles', '2nd chr',
    ],
    '2-chronicles',
  ],
  [['ezra', 'ezr', 'ez'], 'ezra'],
  [['nehemiah', 'neh', 'ne'], 'nehemiah'],
  [['esther', 'esth', 'est', 'es'], 'esther'],
  [['job', 'jb'], 'job'],
  // Operator-locked: 'psalm' singular AND 'psalms' plural both accepted.
  [['psalm', 'psalms', 'ps', 'psa', 'psm', 'pss'], 'psalm'],
  [['proverbs', 'prov', 'prv', 'pro'], 'proverbs'],
  [['ecclesiastes', 'eccl', 'ecc'], 'ecclesiastes'],
  // Operator-locked: 'song of songs' and 'canticles' NOT in map
  // (Catholic-leaning names). parseReference returns null for them.
  [['song of solomon', 'songofsolomon', 'sos', 'song'], 'song-of-solomon'],
  [['isaiah', 'isa', 'is'], 'isaiah'],
  [['jeremiah', 'jer', 'je'], 'jeremiah'],
  [['lamentations', 'lam', 'la'], 'lamentations'],
  [['ezekiel', 'ezek', 'eze', 'ezk'], 'ezekiel'],
  [['daniel', 'dan', 'dn', 'da'], 'daniel'],
  [['hosea', 'hos', 'ho'], 'hosea'],
  [['joel', 'joe', 'jl'], 'joel'],
  [['amos', 'am', 'amo'], 'amos'],
  [['obadiah', 'obad', 'oba', 'ob'], 'obadiah'],
  [['jonah', 'jon', 'jnh'], 'jonah'],
  [['micah', 'mic'], 'micah'],
  [['nahum', 'nah', 'na'], 'nahum'],
  [['habakkuk', 'hab'], 'habakkuk'],
  [['zephaniah', 'zeph', 'zep'], 'zephaniah'],
  [['haggai', 'hag', 'hg'], 'haggai'],
  [['zechariah', 'zech', 'zec'], 'zechariah'],
  [['malachi', 'mal', 'ml'], 'malachi'],
  // ---- New Testament ----
  [['matthew', 'matt', 'mat', 'mt'], 'matthew'],
  [['mark', 'mar', 'mrk', 'mk'], 'mark'],
  [['luke', 'luk', 'lk'], 'luke'],
  [['john', 'jhn', 'jn'], 'john'],
  [['acts', 'act', 'ac'], 'acts'],
  [['romans', 'rom', 'rm', 'ro'], 'romans'],
  [
    [
      '1 corinthians', '1corinthians',
      '1 cor', '1cor', '1co',
      'i corinthians', 'icorinthians',
      'i cor', 'icor',
      'first corinthians', 'firstcorinthians',
      'first cor',
      '1st corinthians', '1st cor',
    ],
    '1-corinthians',
  ],
  [
    [
      '2 corinthians', '2corinthians',
      '2 cor', '2cor', '2co',
      'ii corinthians', 'iicorinthians',
      'ii cor', 'iicor',
      'second corinthians', 'secondcorinthians',
      'second cor',
      '2nd corinthians', '2nd cor',
    ],
    '2-corinthians',
  ],
  [['galatians', 'gal', 'ga'], 'galatians'],
  [['ephesians', 'eph', 'ep'], 'ephesians'],
  [['philippians', 'phil', 'php', 'pp'], 'philippians'],
  [['colossians', 'col', 'co'], 'colossians'],
  [
    [
      '1 thessalonians', '1thessalonians',
      '1 thess', '1thess',
      '1 thes', '1thes', '1th',
      'i thessalonians', 'ithessalonians',
      'i thess', 'ithess',
      'i thes', 'ithes',
      'first thessalonians', 'firstthessalonians',
      'first thess',
      '1st thessalonians', '1st thess',
    ],
    '1-thessalonians',
  ],
  [
    [
      '2 thessalonians', '2thessalonians',
      '2 thess', '2thess',
      '2 thes', '2thes', '2th',
      'ii thessalonians', 'iithessalonians',
      'ii thess', 'iithess',
      'ii thes', 'iithes',
      'second thessalonians', 'secondthessalonians',
      'second thess',
      '2nd thessalonians', '2nd thess',
    ],
    '2-thessalonians',
  ],
  [
    [
      '1 timothy', '1timothy',
      '1 tim', '1tim', '1ti',
      'i timothy', 'itimothy',
      'i tim', 'itim',
      'first timothy', 'firsttimothy',
      'first tim',
      '1st timothy', '1st tim',
    ],
    '1-timothy',
  ],
  [
    [
      '2 timothy', '2timothy',
      '2 tim', '2tim', '2ti',
      'ii timothy', 'iitimothy',
      'ii tim', 'iitim',
      'second timothy', 'secondtimothy',
      'second tim',
      '2nd timothy', '2nd tim',
    ],
    '2-timothy',
  ],
  [['titus', 'tit', 'ti'], 'titus'],
  [['philemon', 'phlm', 'phm'], 'philemon'],
  [['hebrews', 'heb', 'he'], 'hebrews'],
  [['james', 'jas'], 'james'],
  [
    [
      '1 peter', '1peter',
      '1 pet', '1pet', '1pe',
      'i peter', 'ipeter',
      'i pet', 'ipet',
      'first peter', 'firstpeter',
      'first pet',
      '1st peter', '1st pet',
    ],
    '1-peter',
  ],
  [
    [
      '2 peter', '2peter',
      '2 pet', '2pet', '2pe',
      'ii peter', 'iipeter',
      'ii pet', 'iipet',
      'second peter', 'secondpeter',
      'second pet',
      '2nd peter', '2nd pet',
    ],
    '2-peter',
  ],
  [
    [
      '1 john', '1john',
      '1 jn', '1jn',
      'i john', 'ijohn',
      'i jn', 'ijn',
      'first john', 'firstjohn',
      'first jn',
      '1st john', '1st jn',
    ],
    '1-john',
  ],
  [
    [
      '2 john', '2john',
      '2 jn', '2jn',
      'ii john', 'iijohn',
      'ii jn', 'iijn',
      'second john', 'secondjohn',
      'second jn',
      '2nd john', '2nd jn',
    ],
    '2-john',
  ],
  [
    [
      '3 john', '3john',
      '3 jn', '3jn',
      'iii john', 'iiijohn',
      'iii jn', 'iiijn',
      'third john', 'thirdjohn',
      'third jn',
      '3rd john', '3rd jn',
    ],
    '3-john',
  ],
  [['jude', 'jud'], 'jude'],
  // Operator-locked: 'revelation' AND 'revelations' both accepted.
  [['revelation', 'revelations', 'rev', 're'], 'revelation'],
];

export const ALIAS_MAP: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const [aliases, slug] of ALIAS_DATA) {
    for (const a of aliases) {
      if (m.has(a)) {
        throw new Error(`bsb-parser: duplicate alias "${a}"`);
      }
      m.set(a, slug);
    }
  }
  return m;
})();

// Module-load check that every alias resolves to a real canonical
// slug. Catches typos in ALIAS_DATA at first import (probe + first
// browser load).
{
  const validSlugs = new Set(CANONICAL_BOOKS.map((b) => b.slug));
  for (const slug of ALIAS_MAP.values()) {
    if (!validSlugs.has(slug)) {
      throw new Error(
        `bsb-parser: alias map references non-canonical slug "${slug}"`,
      );
    }
  }
}

// Aliases sorted longest-first so the matcher prefers "1 samuel"
// over "1 sam" and "song of solomon" over "song".
const ALIASES_LONGEST_FIRST: readonly string[] = (() => {
  const keys = Array.from(ALIAS_MAP.keys());
  keys.sort((a, b) => b.length - a.length);
  return keys;
})();

function normalizeInput(text: string): string {
  return text
    .trim()
    .toLowerCase()
    // Strip periods that are NOT followed by a digit. Preserves the
    // chapter.verse separator in "Phil 4.6" while still collapsing
    // abbreviation periods like "Phil." → "phil".
    .replace(/\.(?!\d)/g, '')
    .replace(/\s+/g, ' ');
}

const ALPHA_RE = /[a-z]/;

function findBookAlias(
  normalized: string,
): { slug: string; alias: string } | null {
  for (const alias of ALIASES_LONGEST_FIRST) {
    if (!normalized.startsWith(alias)) continue;
    const next = normalized.charAt(alias.length);
    // Word boundary: the character immediately after the alias must
    // not be alphabetic. Digits, whitespace, and end-of-string all
    // qualify so "1sam5:1" still resolves through the "1sam" alias.
    if (next !== '' && ALPHA_RE.test(next)) continue;
    return { slug: ALIAS_MAP.get(alias)!, alias };
  }
  return null;
}

const COLON_RANGE_RE = /^(\d+):(\d+)-(\d+)$/;
const ENDASH_RANGE_RE = /^(\d+):(\d+)–(\d+)$/;
const PERIOD_RE = /^(\d+)\.(\d+)$/;
const COLON_RE = /^(\d+):(\d+)$/;
const SPACE_RE = /^(\d+) (\d+)$/;
const CHAPTER_ONLY_RE = /^(\d+)$/;

function parseChapterVerseRemainder(rem: string): {
  chapter: number;
  verseStart: number | null;
  verseEnd: number | null;
} | null {
  let m: RegExpMatchArray | null;
  if ((m = rem.match(COLON_RANGE_RE)) || (m = rem.match(ENDASH_RANGE_RE))) {
    return {
      chapter: Number(m[1]),
      verseStart: Number(m[2]),
      verseEnd: Number(m[3]),
    };
  }
  if (
    (m = rem.match(PERIOD_RE)) ||
    (m = rem.match(COLON_RE)) ||
    (m = rem.match(SPACE_RE))
  ) {
    const v = Number(m[2]);
    return { chapter: Number(m[1]), verseStart: v, verseEnd: v };
  }
  if ((m = rem.match(CHAPTER_ONLY_RE))) {
    return { chapter: Number(m[1]), verseStart: null, verseEnd: null };
  }
  return null;
}

export function parseReference(text: string): ParsedReference | null {
  if (typeof text !== 'string') return null;
  const normalized = normalizeInput(text);
  if (normalized.length === 0) return null;

  const found = findBookAlias(normalized);
  if (!found) return null;

  const remainder = normalized.slice(found.alias.length).trim();
  if (remainder.length === 0) return null;

  const parsed = parseChapterVerseRemainder(remainder);
  if (!parsed) return null;

  // Structural validation only. The retrieval layer
  // (getChapter/getVerse) handles "this chapter doesn't exist in
  // this book"; the parser only enforces that the parsed numbers
  // are sensible.
  if (parsed.chapter < 1) return null;
  if (parsed.verseStart !== null && parsed.verseStart < 1) return null;
  if (
    parsed.verseStart !== null &&
    parsed.verseEnd !== null &&
    parsed.verseEnd < parsed.verseStart
  ) {
    return null;
  }

  return {
    bookSlug: found.slug,
    chapter: parsed.chapter,
    verseStart: parsed.verseStart,
    verseEnd: parsed.verseEnd,
  };
}
